import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { stopImpersonation } from '@/api/auth';

function remainingLabel(expiresAt: number): string {
  const ms = expiresAt - Date.now();
  if (ms <= 0) return 'expired';
  const mins = Math.ceil(ms / 60_000);
  return mins >= 60 ? '1h' : `${mins} min`;
}

/**
 * Persistent red strip shown while a Gathered platform admin is acting as
 * this organization. Renders nothing for normal owner sessions.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const impersonation = user?.impersonation ?? null;
  const [ending, setEnding] = useState(false);
  const [, forceTick] = useState(0);

  // Re-render every 30s so the countdown stays honest
  useEffect(() => {
    if (!impersonation) return;
    const t = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [impersonation]);

  if (!impersonation) return null;

  const endSession = async () => {
    setEnding(true);
    try {
      await stopImpersonation();
    } finally {
      window.location.replace('/auth/login');
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-red-600 px-4 py-1.5 text-white">
      <div className="flex min-w-0 items-center gap-2 text-xs font-medium">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span className="truncate">
          Gathered admin session — acting as this organization ({impersonation.adminEmail}) ·
          ends in {remainingLabel(impersonation.expiresAt)}
        </span>
      </div>
      <button
        type="button"
        onClick={() => void endSession()}
        disabled={ending}
        className="shrink-0 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-white/25 disabled:opacity-60"
      >
        {ending ? 'Ending…' : 'End session'}
      </button>
    </div>
  );
}
